import React from "react";
import { useIntl } from "react-intl";
import { Column } from "@webbio/components";

import { SyncRunResultDTO, SyncRunResultDTOStatusEnum } from "@app/api/generated";
import { LabelComponent } from "@app/core";
import { Table } from "@app/components/table/table";
import { RUN_STATUS_LABEL_KEY } from "@app/util/run-status";

import { getPlatformName } from "@app/util/platform-name";
import { useProcessedDate } from "@app/util/use-processed-date";
import { RunCounterSummary } from "@app/components/run-counter-summary/run-counter-summary";
import { RUN_COUNTERS } from "../run-counters";

interface IRunsTableProps {
  runs: SyncRunResultDTO[];
  isLoading: boolean;
  onRowClick: (run: SyncRunResultDTO) => void;
}

const RunsTable = ({ runs, isLoading, onRowClick }: IRunsTableProps): JSX.Element => {
  const intl = useIntl();
  const formatProcessedDate = useProcessedDate();

  const onTdClick = (run: SyncRunResultDTO) => onRowClick(run);

  return (
    <Table dataSource={runs} isLoading={isLoading}>
      <Column
        field="runId"
        title={intl.formatMessage({ id: "productPageSync.table.id" })}
        width={70}
        onTdClick={onTdClick}
      />
      <Column
        field="status"
        title={intl.formatMessage({ id: "productPageSync.table.status" })}
        maxWidth={80}
        width={80}
        onTdClick={onTdClick}
        cell={(status: SyncRunResultDTOStatusEnum) => (
          <LabelComponent
            type={status.toLowerCase()}
            label={intl.formatMessage({ id: RUN_STATUS_LABEL_KEY[status] })}
          />
        )}
      />
      <Column
        field="platformId"
        title={intl.formatMessage({ id: "productPageSync.table.platform" })}
        onTdClick={onTdClick}
        cell={(platformId: number) => <>{getPlatformName(intl, platformId)}</>}
        maxWidth={90}
        width={90}
      />
      <Column
        field="finishedAt"
        title={intl.formatMessage({ id: "productPageSync.table.finishedAt" })}
        onTdClick={onTdClick}
        cell={(finishedAt: Date | null) => (
          <span>{finishedAt ? <>{formatProcessedDate(new Date(finishedAt))}</> : <>—</>}</span>
        )}
      />
      <Column
        field="runId"
        title=""
        isNotSortable
        onTdClick={onTdClick}
        cell={(_runId: number, _index: number, run: SyncRunResultDTO) => (
          <RunCounterSummary run={run} counters={RUN_COUNTERS} />
        )}
      />
    </Table>
  );
};

export { RunsTable };
